export interface HeartbeatSemanticRejection {
  geom_id: string;
  feature_id: string;
  rejection_code: string;
  parameter: string;
  occurrence_count: number;
}

type Row = Record<string, unknown>;

function row(value: unknown, path: string): Row {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Row;
}

function exact(value: Row, keys: readonly string[], path: string): void {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  if (actual.length !== expected.length || actual.some((key, index) => key !== expected[index])) {
    throw new Error(`${path} has an unsupported field set`);
  }
}

function text(value: unknown, path: string): string {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`${path} must be non-empty text`);
  }
  return value;
}

function code(value: unknown, path: string): string {
  const result = text(value, path);
  if (!/^[a-z0-9][a-z0-9-]{0,127}$/.test(result)) {
    throw new Error(`${path} is not a bounded code`);
  }
  return result;
}

function integer(value: unknown, path: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0 || (value as number) > 1_000_000) {
    throw new Error(`${path} must be a bounded nonnegative integer`);
  }
  return value as number;
}

export function parseHeartbeatSemanticRejections(
  value: unknown,
  path: string,
): readonly HeartbeatSemanticRejection[] {
  if (!Array.isArray(value) || value.length > 512) {
    throw new Error(`${path} must be a bounded array`);
  }
  const result = value.map((child, index) => {
    const itemPath = `${path}[${index}]`;
    const source = row(child, itemPath);
    exact(source, ["geom_id", "feature_id", "rejection_code", "parameter", "occurrence_count"], itemPath);
    const geomId = text(source.geom_id, `${itemPath}.geom_id`);
    const featureId = text(source.feature_id, `${itemPath}.feature_id`);
    const parameter = text(source.parameter, `${itemPath}.parameter`);
    if (geomId.length > 256) throw new Error(`${itemPath}.geom_id is overbound`);
    if (!/^[a-z0-9][a-z0-9._-]{0,127}$/.test(featureId)) {
      throw new Error(`${itemPath}.feature_id is invalid`);
    }
    if (!/^[A-Za-z][A-Za-z0-9_]{0,63}$/.test(parameter)) {
      throw new Error(`${itemPath}.parameter is invalid`);
    }
    const occurrenceCount = integer(source.occurrence_count, `${itemPath}.occurrence_count`);
    if (occurrenceCount < 1) throw new Error(`${itemPath}.occurrence_count must be positive`);
    return {
      geom_id: geomId,
      feature_id: featureId,
      rejection_code: code(source.rejection_code, `${itemPath}.rejection_code`),
      parameter,
      occurrence_count: occurrenceCount,
    };
  });
  const keys = result.map((item) => `${item.geom_id}\u001f${item.feature_id}\u001f${item.parameter}`);
  if (keys.some((key, index) => index > 0 && keys[index - 1]! >= key)) {
    throw new Error(`${path} must be sorted and unique`);
  }
  return result;
}
